class MouldSolveFinder extends HTMLElement {
  connectedCallback() {
    this.choices = Array.from(this.querySelectorAll('[data-ms-choice]'));
    this.results = Array.from(this.querySelectorAll('[data-ms-result]'));

    this.choices.forEach((choice) => {
      choice.addEventListener('click', () => this.showResult(choice));
    });
  }

  showResult(choice) {
    const target = choice.dataset.msChoice;

    this.choices.forEach((item) => {
      item.setAttribute('aria-pressed', String(item === choice));
    });

    this.results.forEach((result) => {
      result.hidden = result.dataset.msResult !== target;
    });

    const prompt = this.querySelector('[data-ms-prompt]');
    if (prompt) prompt.hidden = true;

    const activeResult = this.querySelector(`[data-ms-result="${target}"]`);
    activeResult?.focus({ preventScroll: true });
  }
}

class MouldSolveAbTest extends HTMLElement {
  connectedCallback() {
    this.storageKey = this.dataset.msAbStorageKey || 'mould-solve-ab-test';
    this.mode = this.dataset.msAbMode || 'off';
    this.hasVariantB = this.dataset.msAbHasVariantB === 'true';
    this.variants = Array.from(this.querySelectorAll('[data-ms-variant]'));

    if (!this.variants.length) return;

    const selectedVariant = this.selectVariant();
    this.applyVariant(selectedVariant);
  }

  selectVariant() {
    if (!this.hasVariantB) return 'a';

    const searchParams = new URLSearchParams(window.location.search);
    const previewVariant = searchParams.get('ms_ab');
    if (previewVariant === 'a' || previewVariant === 'b') {
      window.localStorage.setItem(this.storageKey, previewVariant);
      return previewVariant;
    }

    if (this.mode === 'force_b') {
      window.localStorage.setItem(this.storageKey, 'b');
      return 'b';
    }

    if (this.mode === 'split') {
      const storedVariant = window.localStorage.getItem(this.storageKey);
      if (storedVariant === 'a' || storedVariant === 'b') {
        return storedVariant;
      }

      const assignedVariant = Math.random() < 0.5 ? 'a' : 'b';
      window.localStorage.setItem(this.storageKey, assignedVariant);
      return assignedVariant;
    }

    window.localStorage.setItem(this.storageKey, 'a');
    return 'a';
  }

  applyVariant(activeVariant) {
    this.dataset.msAbActiveVariant = activeVariant;

    this.variants.forEach((variant) => {
      variant.hidden = variant.dataset.msVariant !== activeVariant;
    });

    const activeHeading = this.querySelector(
      `[data-ms-variant="${activeVariant}"] h1, [data-ms-variant="${activeVariant}"] h2`
    );
    if (activeHeading?.id) {
      this.setAttribute('aria-labelledby', activeHeading.id);
    }

    document.dispatchEvent(
      new CustomEvent('mould-solve:variant-change', {
        detail: {
          test: this.dataset.msAbTest || 'homepage',
          variant: activeVariant,
        },
      })
    );
  }
}

class MouldSolveProofToast extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-ms-proof-item]'));
    this.closeButton = this.querySelector('[data-ms-proof-close]');
    this.delay = Number(this.dataset.msProofDelay || 3600);
    this.interval = Number(this.dataset.msProofInterval || 6200);
    this.storageKey = this.dataset.msProofStorageKey || 'mould-solve-proof-toast';
    this.currentIndex = -1;
    this.timeoutId = null;
    this.intervalId = null;
    this.dismissed = false;

    if (!this.items.length) return;
    if (this.shouldSuppress()) {
      this.hidden = true;
      return;
    }

    this.items.forEach((item) => {
      item.hidden = true;
    });

    this.hidden = true;

    this.closeButton?.addEventListener('click', () => this.dismiss());
    this.addEventListener('mouseenter', this.pauseRotation);
    this.addEventListener('mouseleave', this.resumeRotation);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('mould-solve:variant-change', this.handleVariantChange);

    if (this.isActiveVariant()) {
      this.start();
    }
  }

  disconnectedCallback() {
    this.clearTimers();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('mould-solve:variant-change', this.handleVariantChange);
    this.removeEventListener('mouseenter', this.pauseRotation);
    this.removeEventListener('mouseleave', this.resumeRotation);
  }

  handleVariantChange = (event) => {
    const variant = event.detail?.variant;

    if ((variant === 'a' || variant === 'b') && !this.dismissed) {
      this.start();
      return;
    }

    this.hide();
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.pauseRotation();
    } else if (!this.dismissed && this.isActiveVariant()) {
      this.resumeRotation();
    }
  };

  isActiveVariant() {
    const variant = this.closest('[data-ms-variant]');
    if (!variant) return true;
    return !variant.hidden;
  }

  shouldSuppress() {
    try {
      return window.localStorage.getItem(this.storageKey) === 'dismissed';
    } catch (_error) {
      return false;
    }
  }

  start() {
    if (this.started || !this.isActiveVariant()) return;

    this.started = true;
    this.timeoutId = window.setTimeout(() => {
      this.showNext();
      this.intervalId = window.setInterval(() => this.showNext(), Math.max(this.interval, 3000));
    }, Math.max(this.delay, 0));
  }

  showNext() {
    if (!this.isActiveVariant() || this.dismissed) return;

    this.currentIndex = (this.currentIndex + 1) % this.items.length;
    this.items.forEach((item, index) => {
      item.hidden = index !== this.currentIndex;
    });

    this.hidden = false;
    this.classList.add('is-visible');
  }

  hide() {
    this.hidden = true;
    this.classList.remove('is-visible');
    this.items.forEach((item) => {
      item.hidden = true;
    });
    this.clearTimers();
    this.started = false;
  }

  dismiss() {
    this.dismissed = true;
    this.hide();

    try {
      window.localStorage.setItem(this.storageKey, 'dismissed');
    } catch (_error) {
      // Ignore storage failures.
    }
  }

  pauseRotation = () => {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  };

  resumeRotation = () => {
    if (this.dismissed || document.hidden || !this.isActiveVariant() || this.intervalId) return;
    if (this.currentIndex < 0) return;

    this.intervalId = window.setInterval(() => this.showNext(), Math.max(this.interval, 3000));
  };

  clearTimers() {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

class MouldSolveOfferPopup extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.ms-offer-popup__panel');
    this.closeButtons = Array.from(this.querySelectorAll('[data-ms-popup-close]'));
    this.copyButton = this.querySelector('[data-ms-popup-copy]');
    this.codeElement = this.querySelector('[data-ms-popup-code]');
    this.successState = this.querySelector('.ms-offer-popup__success');
    this.storageKey = 'mould-solve-offer-popup';
    this.hideForCustomers = this.dataset.msPopupHideForCustomers === 'true';
    this.homeOnly = this.dataset.msPopupHomeOnly === 'true';
    this.delaySeconds = Number(this.dataset.msPopupDelay || 6);
    this.expiryDays = Number(this.dataset.msPopupExpiryDays || 14);
    this.isSuccess = this.successState instanceof HTMLElement && !this.successState.hidden;

    if (!(this.panel instanceof HTMLElement)) return;
    if (this.hideForCustomers && document.documentElement.hasAttribute('data-customer-logged-in')) return;
    if (this.homeOnly && window.location.pathname !== '/') return;

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.copyButton?.addEventListener('click', async () => {
      const code = this.codeElement?.textContent?.trim();
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code);
        this.copyButton.textContent = 'Copied';
      } catch (_error) {
        this.copyButton.textContent = code;
      }
    });

    document.addEventListener('keydown', this.handleKeydown);

    if (this.isSuccess) {
      this.markSubmitted();
      this.open();
      return;
    }

    if (this.shouldSuppress()) return;

    window.setTimeout(() => this.open(), Math.max(this.delaySeconds, 0) * 1000);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.panel && !this.panel.hidden) {
      this.close();
    }
  };

  shouldSuppress() {
    try {
      const rawValue = window.localStorage.getItem(this.storageKey);
      if (!rawValue) return false;

      const parsed = JSON.parse(rawValue);
      if (parsed.status === 'submitted') return true;
      if (typeof parsed.expiresAt !== 'number') return false;

      if (Date.now() < parsed.expiresAt) return true;
      window.localStorage.removeItem(this.storageKey);
      return false;
    } catch (_error) {
      return false;
    }
  }

  open() {
    this.panel.hidden = false;
    document.body.classList.add('ms-offer-popup-open');
  }

  close() {
    this.panel.hidden = true;
    document.body.classList.remove('ms-offer-popup-open');

    if (!this.isSuccess) {
      this.setDismissed();
    }
  }

  setDismissed() {
    try {
      const expiresAt = Date.now() + this.expiryDays * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(this.storageKey, JSON.stringify({ status: 'dismissed', expiresAt }));
    } catch (_error) {
      // Ignore storage failures.
    }
  }

  markSubmitted() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify({ status: 'submitted' }));
    } catch (_error) {
      // Ignore storage failures.
    }
  }
}

class MouldSolveAssistant extends HTMLElement {
  connectedCallback() {
    this.launcher = this.querySelector('.ms-assistant__launcher');
    this.panel = this.querySelector('.ms-assistant__panel');
    this.closeButton = this.querySelector('.ms-assistant__close');
    this.messages = this.querySelector('.ms-assistant__messages');
    this.actions = this.querySelector('.ms-assistant__actions');
    this.form = this.querySelector('.ms-assistant__form');
    this.input = this.querySelector('.ms-assistant__input');

    this.urls = {
      home: this.dataset.rootUrl,
      support: this.dataset.supportUrl,
      kits: this.dataset.kitsUrl,
      sprays: this.dataset.spraysUrl,
      prevention: this.dataset.preventionUrl,
      paint: this.dataset.paintUrl,
      outdoor: this.dataset.outdoorUrl,
      advice: this.dataset.adviceUrl,
      articlePaint: this.dataset.articlePaintUrl,
      articleReturning: this.dataset.articleReturningUrl,
      articleBathroom: this.dataset.articleBathroomUrl,
      articleStain: this.dataset.articleStainUrl,
      articleOutdoor: this.dataset.articleOutdoorUrl,
      articleKits: this.dataset.articleKitsUrl,
    };

    this.assistantTitle = this.dataset.title || 'Product Help';
    this.assistantOpeningMessage =
      this.dataset.openingMessage ||
      'Tell us where the problem is or ask a question. I can point you toward the right treatment category or a safer support route.';

    if (this.panel && this.assistantTitle) {
      this.panel.setAttribute('aria-label', this.assistantTitle);
    }

    this.faqEntries = [
      {
        keywords: ['paint', 'before painting', 'paint over mould', 'should i remove mould before painting'],
        title: 'Should I remove mould before painting?',
        body: 'Yes. Clean the affected area, let it dry fully, deal with the moisture source, then use the right preparation or anti-mould paint for the next stage.',
        linkLabel: 'View anti-mould paint',
        linkUrl: this.urls.paint,
        secondaryLinkLabel: 'Read the full guide',
        secondaryLinkUrl: this.urls.articlePaint,
      },
      {
        keywords: ['returning', 'coming back', 'keeps returning', 'recurring'],
        title: 'Why does mould keep coming back?',
        body: 'Recurring mould usually means moisture is still available through condensation, poor ventilation, a leak or another damp issue. Treatment helps, but the moisture cause also needs attention.',
        linkLabel: 'View prevention products',
        linkUrl: this.urls.prevention,
        secondaryLinkLabel: 'Read the full guide',
        secondaryLinkUrl: this.urls.articleReturning,
      },
      {
        keywords: ['kit', 'kits', 'full system', 'whole problem', 'starter'],
        title: 'Should I choose a kit or individual products?',
        body: 'Treatment kits are best when you want a clearer remove, treat and protect route without piecing together separate products yourself.',
        linkLabel: 'Compare treatment kits',
        linkUrl: this.urls.kits,
        secondaryLinkLabel: 'Read the full guide',
        secondaryLinkUrl: this.urls.articleKits,
      },
      {
        keywords: ['bathroom', 'grout', 'silicone', 'tiles'],
        title: 'Which product is best for bathroom mould?',
        body: 'For visible mould on suitable washable bathroom surfaces, start with a targeted mould removal spray and follow the product directions carefully.',
        linkLabel: 'View mould removal sprays',
        linkUrl: this.urls.sprays,
        secondaryLinkLabel: 'Read the bathroom guide',
        secondaryLinkUrl: this.urls.articleBathroom,
      },
      {
        keywords: ['outside', 'outdoor', 'patio', 'paving', 'driveway'],
        title: 'What should I use on patios or driveways?',
        body: 'Use a treatment designed for exterior hard surfaces affected by organic growth and weather staining.',
        linkLabel: 'View outdoor cleaning',
        linkUrl: this.urls.outdoor,
        secondaryLinkLabel: 'Read the outdoor guide',
        secondaryLinkUrl: this.urls.articleOutdoor,
      },
      {
        keywords: ['stain', 'water stain', 'damp mark', 'damp marks'],
        title: 'What about damp marks or water stains?',
        body: 'Once the moisture source is fixed and the surface is fully dry, use a suitable preparation or stain blocking product before the final decorative coating.',
        linkLabel: 'View prevention and primers',
        linkUrl: this.urls.prevention,
        secondaryLinkLabel: 'Read the full guide',
        secondaryLinkUrl: this.urls.articleStain,
      },
      {
        keywords: ['which product', 'what product', 'where do i start', 'not sure', 'help choose'],
        title: 'Where should I start?',
        body: 'Start with the location of the problem and the stage you are at: remove visible mould, prevent regrowth, prepare a stained surface, or finish with anti-mould paint.',
        linkLabel: 'Compare treatment kits',
        linkUrl: this.urls.kits,
      },
    ];

    this.flowMap = {
      start: [
        { label: 'Help me choose a product', action: 'choose' },
        { label: 'Ask a question', action: 'faq' },
        { label: 'Read mould advice', action: 'advice' },
        { label: 'I still need support', action: 'support' },
      ],
      choose: [
        { label: 'Painted walls or ceilings', action: 'walls' },
        { label: 'Bathroom, grout or silicone', action: 'bathroom' },
        { label: 'Mould that keeps returning', action: 'recurring' },
        { label: 'Damp marks or water stains', action: 'stain' },
        { label: 'Patio, paving or driveway', action: 'outdoor' },
        { label: 'Not sure where to start', action: 'unsure' },
      ],
      advice: [
        { label: 'Advice for painting after mould', action: 'advice-paint' },
        { label: 'Advice for recurring mould', action: 'advice-returning' },
        { label: 'Bathroom mould advice', action: 'advice-bathroom' },
        { label: 'Treatment kit guide', action: 'advice-kits' },
      ],
      walls: [
        { label: 'Remove visible mould', action: 'walls-remove' },
        { label: 'Help stop it returning', action: 'walls-prevent' },
        { label: 'Paint after treatment', action: 'walls-paint' },
      ],
      bathroom: [
        { label: 'I need to remove visible mould', action: 'bathroom-remove' },
        { label: 'It keeps returning after cleaning', action: 'bathroom-recurring' },
      ],
      recurring: [
        { label: 'I need a full treatment route', action: 'recurring-full' },
        { label: 'I need prevention after cleaning', action: 'recurring-prevent' },
      ],
      faq: [
        { label: 'Should I remove mould before painting?', action: 'faq-paint' },
        { label: 'Why does mould keep coming back?', action: 'faq-returning' },
        { label: 'Do I need a kit or individual products?', action: 'faq-kits' },
        { label: 'What order should I use the products in?', action: 'faq-order' },
      ],
    };

    this.responseMap = {
      'walls-remove': {
        title: 'Start with mould removal sprays',
        body: 'For visible mould on painted walls or ceilings, begin with a dedicated removal product before moving on to prevention or redecoration.',
        linkLabel: 'View mould removal sprays',
        linkUrl: this.urls.sprays,
        secondaryLinkLabel: 'Read wall and ceiling advice',
        secondaryLinkUrl: `${this.urls.advice}/how-to-remove-mould-from-painted-walls-and-ceilings`,
      },
      'walls-prevent': {
        title: 'Use prevention products after cleaning',
        body: 'Once the visible mould is removed and the area is dry, add a preventative stage to help reduce regrowth.',
        linkLabel: 'View prevention products',
        linkUrl: this.urls.prevention,
        secondaryLinkLabel: 'Read the recurring mould guide',
        secondaryLinkUrl: this.urls.articleReturning,
      },
      'walls-paint': {
        title: 'Finish with anti-mould paint',
        body: 'Use anti-mould paint only after the surface has been treated properly, allowed to dry and any moisture source has been addressed.',
        linkLabel: 'View anti-mould paint',
        linkUrl: this.urls.paint,
        secondaryLinkLabel: 'Read the prep guide',
        secondaryLinkUrl: this.urls.articlePaint,
      },
      'bathroom-remove': {
        title: 'Start with mould removal sprays',
        body: 'Bathroom mould on suitable washable surfaces usually starts with a dedicated removal spray, followed by good drying and ventilation.',
        linkLabel: 'View mould removal sprays',
        linkUrl: this.urls.sprays,
        secondaryLinkLabel: 'Read the bathroom guide',
        secondaryLinkUrl: this.urls.articleBathroom,
      },
      'bathroom-recurring': {
        title: 'Treat and help stop it returning',
        body: 'If bathroom mould keeps returning, remove the visible growth first and then focus on prevention, airflow and moisture control.',
        linkLabel: 'View prevention products',
        linkUrl: this.urls.prevention,
        secondaryLinkLabel: 'Read the recurring mould guide',
        secondaryLinkUrl: this.urls.articleReturning,
      },
      'recurring-full': {
        title: 'Start with a treatment kit',
        body: 'Recurring mould usually needs more than one product. A treatment kit gives you a clearer remove, treat and protect route.',
        linkLabel: 'Compare treatment kits',
        linkUrl: this.urls.kits,
        secondaryLinkLabel: 'Read the kit guide',
        secondaryLinkUrl: this.urls.articleKits,
      },
      'recurring-prevent': {
        title: 'Use prevention products',
        body: 'If the area has already been cleaned and dried, a prevention product is the right next step before any final finish.',
        linkLabel: 'View prevention products',
        linkUrl: this.urls.prevention,
        secondaryLinkLabel: 'Read the recurring mould guide',
        secondaryLinkUrl: this.urls.articleReturning,
      },
      stain: {
        title: 'Use preparation for damp marks and stains',
        body: 'Once the underlying moisture issue is fixed and the surface is dry, prepare stained areas before the final decorative coating.',
        linkLabel: 'View prevention and primers',
        linkUrl: this.urls.prevention,
        secondaryLinkLabel: 'Read the stain guide',
        secondaryLinkUrl: this.urls.articleStain,
      },
      outdoor: {
        title: 'Use outdoor and driveway cleaning',
        body: 'For patios, paving and driveways, use a cleaner designed for exterior hard surfaces affected by organic growth and weather staining.',
        linkLabel: 'View outdoor cleaning',
        linkUrl: this.urls.outdoor,
        secondaryLinkLabel: 'Read the outdoor guide',
        secondaryLinkUrl: this.urls.articleOutdoor,
      },
      unsure: {
        title: 'Start with the treatment kits',
        body: 'If you are unsure which stage you need first, compare the treatment kits for a simpler starting point.',
        linkLabel: 'Compare treatment kits',
        linkUrl: this.urls.kits,
        secondaryLinkLabel: 'Browse all mould advice',
        secondaryLinkUrl: this.urls.advice,
      },
      'advice-paint': {
        title: 'Read the paint preparation guide',
        body: 'This article explains what should happen before anti-mould paint and why decoration should come after cleaning, drying and preparation.',
        linkLabel: 'Read the guide',
        linkUrl: this.urls.articlePaint,
      },
      'advice-returning': {
        title: 'Read the recurring mould guide',
        body: 'This article explains why mould returns after cleaning and how to think about moisture control alongside treatment.',
        linkLabel: 'Read the guide',
        linkUrl: this.urls.articleReturning,
      },
      'advice-bathroom': {
        title: 'Read the bathroom mould guide',
        body: 'This article covers grout, silicone and washable bathroom surfaces, plus when treatment alone is not enough.',
        linkLabel: 'Read the guide',
        linkUrl: this.urls.articleBathroom,
      },
      'advice-kits': {
        title: 'Read the treatment kit guide',
        body: 'This article helps customers decide whether they need a full remove, treat and protect route or a single product stage.',
        linkLabel: 'Read the guide',
        linkUrl: this.urls.articleKits,
      },
      'faq-paint': {
        title: 'Remove mould before painting',
        body: 'Clean the affected area, let it dry and address the moisture source before using preparation or anti-mould paint.',
        linkLabel: 'View anti-mould paint',
        linkUrl: this.urls.paint,
      },
      'faq-returning': {
        title: 'Recurring mould needs moisture control too',
        body: 'Surface treatment matters, but mould often returns because moisture remains available through condensation, poor ventilation or leaks.',
        linkLabel: 'View prevention products',
        linkUrl: this.urls.prevention,
      },
      'faq-kits': {
        title: 'Kits are best for unsure customers',
        body: 'Choose a kit when you want a coordinated route rather than selecting separate products one by one.',
        linkLabel: 'Compare treatment kits',
        linkUrl: this.urls.kits,
      },
      'faq-order': {
        title: 'The usual order is remove, treat, protect',
        body: 'Remove visible mould first, prepare or prevent as needed, then finish with the final protective coating on a suitable dry surface.',
        linkLabel: 'Compare treatment kits',
        linkUrl: this.urls.kits,
        secondaryLinkLabel: 'Browse advice articles',
        secondaryLinkUrl: this.urls.advice,
      },
      support: {
        title: 'Support is the safest next step',
        body: 'If the surface is unusual, the mould is extensive, or you are unsure which route applies, contact Mould Solve for manual guidance.',
        linkLabel: 'Contact support',
        linkUrl: this.urls.support,
        secondaryLinkLabel: 'Browse the advice hub',
        secondaryLinkUrl: this.urls.advice,
      },
    };

    this.launcher?.addEventListener('click', () => this.toggle());
    this.closeButton?.addEventListener('click', () => this.close());
    this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
    document.addEventListener('mould-solve:open-assistant', this.handleExternalOpen);

    this.reset();
  }

  disconnectedCallback() {
    document.removeEventListener('mould-solve:open-assistant', this.handleExternalOpen);
  }

  handleExternalOpen = () => {
    this.open();
  };

  reset() {
    this.messages.innerHTML = '';
    this.actions.innerHTML = '';
    this.addAssistantMessage(this.assistantOpeningMessage);
    this.renderActions('start');
  }

  toggle() {
    if (this.panel.hidden) {
      this.open();
      return;
    }
    this.close();
  }

  open() {
    this.panel.hidden = false;
    this.launcher?.setAttribute('aria-expanded', 'true');
    this.input?.focus({ preventScroll: true });
  }

  close() {
    this.panel.hidden = true;
    this.launcher?.setAttribute('aria-expanded', 'false');
  }

  addAssistantMessage(text, response) {
    const message = document.createElement('article');
    message.className = 'ms-assistant__message ms-assistant__message--assistant';

    const body = document.createElement('p');
    body.textContent = text;
    message.append(body);

    if (response?.title) {
      const title = document.createElement('h3');
      title.textContent = response.title;
      message.append(title);
    }

    if (response?.body) {
      const copy = document.createElement('p');
      copy.textContent = response.body;
      message.append(copy);
    }

    if (response?.linkLabel && response?.linkUrl) {
      const link = document.createElement('a');
      link.className = 'ms-assistant__link';
      link.href = response.linkUrl;
      link.textContent = response.linkLabel;
      message.append(link);
    }

    if (response?.secondaryLinkLabel && response?.secondaryLinkUrl) {
      const secondaryLink = document.createElement('a');
      secondaryLink.className = 'ms-assistant__link';
      secondaryLink.href = response.secondaryLinkUrl;
      secondaryLink.textContent = response.secondaryLinkLabel;
      message.append(secondaryLink);
    }

    this.messages.append(message);
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  addUserMessage(text) {
    const message = document.createElement('article');
    message.className = 'ms-assistant__message ms-assistant__message--user';

    const body = document.createElement('p');
    body.textContent = text;
    message.append(body);

    this.messages.append(message);
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  renderActions(key) {
    const options = this.flowMap[key] || [];
    this.actions.innerHTML = '';

    options.forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ms-assistant__action';
      button.textContent = option.label;
      button.addEventListener('click', () => this.handleAction(option));
      this.actions.append(button);
    });

    const restart = document.createElement('button');
    restart.type = 'button';
    restart.className = 'ms-assistant__reset';
    restart.textContent = 'Start again';
    restart.addEventListener('click', () => this.reset());
    this.actions.append(restart);
  }

  handleAction(option) {
    this.open();
    this.addUserMessage(option.label);

    if (this.flowMap[option.action]) {
      const prompt = this.getPrompt(option.action);
      this.addAssistantMessage(prompt);
      this.renderActions(option.action);
      return;
    }

    const response = this.responseMap[option.action] || this.responseMap.support;
    this.addAssistantMessage('Here is the best starting point.', response);
    this.renderActions('start');
  }

  getPrompt(key) {
    switch (key) {
      case 'choose':
        return 'Where are you seeing the problem most clearly?';
      case 'walls':
        return 'What are you trying to do on that surface?';
      case 'bathroom':
        return 'Which bathroom issue sounds closest?';
      case 'recurring':
        return 'Which stage are you at now?';
      case 'faq':
        return 'Choose a common question or type your own below.';
      case 'advice':
        return 'Choose a guide to read or use the question box below.';
      default:
        return 'Choose the closest match.';
    }
  }

  handleSubmit(event) {
    event.preventDefault();

    const question = this.input?.value.trim();
    if (!question) return;

    this.open();
    this.addUserMessage(question);
    this.input.value = '';

    const response = this.findFaqMatch(question);
    if (response) {
      this.addAssistantMessage('Here’s the closest answer I can give from the current guidance.', response);
    } else {
      this.addAssistantMessage('I can help with treatment routes, mould returning, painting, kits, bathroom surfaces and outdoor cleaning. You can also browse the advice hub for detailed guides.', this.responseMap.support);
    }

    this.renderActions('start');
  }

  findFaqMatch(question) {
    const query = question.toLowerCase();

    return (
      this.faqEntries.find((entry) => {
        return entry.keywords.some((keyword) => query.includes(keyword));
      }) || null
    );
  }
}

if (!customElements.get('mould-solve-finder')) {
  customElements.define('mould-solve-finder', MouldSolveFinder);
}

if (!customElements.get('mould-solve-ab-test')) {
  customElements.define('mould-solve-ab-test', MouldSolveAbTest);
}

if (!customElements.get('mould-solve-offer-popup')) {
  customElements.define('mould-solve-offer-popup', MouldSolveOfferPopup);
}

if (!customElements.get('mould-solve-proof-toast')) {
  customElements.define('mould-solve-proof-toast', MouldSolveProofToast);
}

if (!customElements.get('mould-solve-assistant')) {
  customElements.define('mould-solve-assistant', MouldSolveAssistant);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-ms-open-assistant]');
  if (!trigger) return;

  event.preventDefault();
  document.dispatchEvent(new CustomEvent('mould-solve:open-assistant'));
});
